import React from 'react';
import {Vector3, NormalizedColor, HTMLColor} from './types.ts';
import {FormGroup, Label, Input, Button} from 'reactstrap';
import SpatialCoordinateEditor from './SpatialCoordinateEditor.tsx';
import ColorPickerComponent from '../shared/ColorPicker.tsx';
import useDraft from './useDraft.ts';

interface DashedLinesEditorProps {
    points: Vector3[];
    dashSize: number;
    gapSize: number;
    dashNb?: number;
    colors?: (NormalizedColor | HTMLColor)[];
    onPointsChange: (points: Vector3[]) => void;
    onDashSizeChange: (dashSize: number) => void;
    onGapSizeChange: (gapSize: number) => void;
    onDashNbChange: (dashNb: number | undefined) => void;
    onColorsChange: (
        colors: (NormalizedColor | HTMLColor)[] | undefined
    ) => void;
}

const DashedLinesEditor: React.FC<DashedLinesEditorProps> = ({
    points,
    dashSize,
    gapSize,
    dashNb,
    colors,
    onPointsChange,
    onDashSizeChange,
    onGapSizeChange,
    onDashNbChange,
    onColorsChange
}) => {
    const [currentPoints, setCurrentPoints] = useDraft<Vector3[]>(points);
    const [currentColors, setCurrentColors] = useDraft<
        (NormalizedColor | HTMLColor)[] | undefined
    >(colors);

    const handlePointChange = (index: number, coordinate: Vector3) => {
        const updatedPoints = [...currentPoints];
        updatedPoints[index] = coordinate;
        setCurrentPoints(updatedPoints);
    };

    const handleColorChange = (
        index: number,
        color: NormalizedColor | HTMLColor | undefined
    ) => {
        if (currentColors) {
            const updatedColors = [...currentColors];
            updatedColors[index] = color || '#000000';
            setCurrentColors(updatedColors);
        }
    };

    const handleDashSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onDashSizeChange(isNaN(value) ? 0 : value);
    };

    const handleGapSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onGapSizeChange(isNaN(value) ? 0 : value);
    };

    const handleDashNbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        onDashNbChange(isNaN(value) ? undefined : value);
    };

    const addPoint = () => {
        const newPoint: Vector3 = {x: 0, y: 0, z: 0};
        setCurrentPoints([...currentPoints, newPoint]);
        if (currentColors) {
            const newColor: HTMLColor = '#000000';
            setCurrentColors([...currentColors, newColor]);
        }
    };

    const removePoint = (index: number) => {
        const updatedPoints = currentPoints.filter((_, i) => i !== index);
        setCurrentPoints(updatedPoints);
        if (currentColors) {
            const updatedColors = currentColors.filter((_, i) => i !== index);
            setCurrentColors(updatedColors);
        }
    };

    const applyChanges = () => {
        onPointsChange(currentPoints);
        onColorsChange(currentColors);
    };

    return (
        <>
            <FormGroup>
                <Label>Dash Size:</Label>
                <Input
                    type="number"
                    step="0.1"
                    value={dashSize}
                    onChange={handleDashSizeChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Gap Size:</Label>
                <Input
                    type="number"
                    step="0.1"
                    value={gapSize}
                    onChange={handleGapSizeChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Dash Number:</Label>
                <Input
                    type="number"
                    step="1"
                    value={dashNb ?? ''}
                    onChange={handleDashNbChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Points:</Label>
                {currentPoints.map((point, index) => (
                    <div key={index}>
                        <SpatialCoordinateEditor
                            label={`Point ${index + 1}`}
                            coordinate={point}
                            onChange={coordinate =>
                                handlePointChange(index, coordinate)
                            }
                        />
                        {currentColors && (
                            <ColorPickerComponent
                                color={currentColors[index] as string}
                                onChange={color =>
                                    handleColorChange(index, color)
                                }
                            />
                        )}
                        <Button
                            color="danger"
                            onClick={() => removePoint(index)}
                        >
                            Remove Point
                        </Button>
                    </div>
                ))}
                <Button color="primary" onClick={addPoint}>
                    Add Point
                </Button>
                <Button color="success" onClick={applyChanges}>
                    Apply Changes
                </Button>
            </FormGroup>
        </>
    );
};

export default DashedLinesEditor;
